import { useEffect, useState } from "react";
import type { AgentEvent } from "../../../lib/types";
import { cn } from "../../../lib/cn";
import { StatusBadge, type ToolStatus } from "../../shared/StatusBadge";
import { CopyButton } from "./CopyControls";
import { Collapse, Disclosure, IconChip, MonoBlock } from "./primitives";
import {
  artifactVerb,
  fileName,
  toolFilePath,
  toolMeta,
  toolWriteContent,
} from "./toolMeta";

const PREVIEW_MAX = 6000;

/**
 * A Write/Edit call rendered as a file artifact: the filename stands out, the
 * directory trails muted, a created/updated badge says what happened, and the
 * path is copyable. When the call carries content, it's previewable inline
 * (collapsed, truncated for huge files). Makes "the agent changed a file"
 * legible at a glance instead of buried in a tool-result blob.
 */
export function ArtifactCard({
  event,
  result,
}: {
  event: AgentEvent;
  result?: AgentEvent;
}) {
  const name = String(event.payload.name ?? "write");
  const input = event.payload.input as Record<string, unknown> | undefined;
  const Icon = toolMeta(name).Icon;
  const path = toolFilePath(input) ?? "";
  const base = path ? fileName(path) : toolMeta(name).label;
  const dir = path ? path.slice(0, path.length - base.length) : "";

  const content = toolWriteContent(input);
  const preview = content ? content.slice(0, PREVIEW_MAX) : null;
  const truncated = Boolean(content && content.length > PREVIEW_MAX);

  const hasResult = Boolean(result);
  const isError = Boolean(result?.payload.is_error);
  const resultContent = String(result?.payload.content ?? "");
  const status: ToolStatus = !hasResult ? "running" : isError ? "error" : "success";
  const badgeLabel =
    status === "success" ? artifactVerb(name) : status === "running" ? "writing" : undefined;

  const hasDetails = Boolean(preview) || hasResult;
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    if (isError) {
      setOpen(true);
      setOpened(true);
    }
  }, [isError]);
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) setOpened(true);
      return next;
    });
  };

  return (
    <div
      className="group rounded-md border border-border-subtle bg-bg-surface transition-colors duration-fast ease-out hover:border-border-default"
      style={isError ? { borderColor: "var(--status-error)" } : undefined}
    >
      <div className="flex items-center pr-1.5">
        <button
          type="button"
          aria-expanded={hasDetails ? open : undefined}
          onClick={hasDetails ? toggle : undefined}
          disabled={!hasDetails}
          className={cn(
            "flex-1 min-w-0 flex items-center gap-2 px-2.5 py-1.5 text-left rounded-md",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            !hasDetails && "cursor-default",
          )}
        >
          {hasDetails ? (
            <Disclosure open={open} />
          ) : (
            <span className="w-3.5 shrink-0" aria-hidden="true" />
          )}
          <IconChip>
            <Icon size={13} />
          </IconChip>
          <span
            className="flex min-w-0 flex-1 items-baseline overflow-hidden font-mono"
            title={path || base}
          >
            {dir && (
              <span className="truncate text-[11.5px] text-text-tertiary">{dir}</span>
            )}
            <span className="shrink-0 text-[13px] text-text-primary">{base}</span>
          </span>
          <StatusBadge key={status} status={status} label={badgeLabel} />
        </button>
        {path && (
          <CopyButton
            value={path}
            label="Copy path"
            className="self-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          />
        )}
      </div>

      {hasDetails && (
        <Collapse open={open}>
          {(open || opened) && (
            <div className="px-2.5 pb-2.5 pt-0.5 space-y-2">
              {preview && (
                <MonoBlock label={truncated ? "preview (truncated)" : "preview"}>
                  {preview}
                </MonoBlock>
              )}
              {hasResult && (isError || !preview) && (
                <MonoBlock
                  label={isError ? "error" : "result"}
                  tone={isError ? "error" : "default"}
                >
                  {resultContent || "(empty)"}
                </MonoBlock>
              )}
            </div>
          )}
        </Collapse>
      )}
    </div>
  );
}
